import { getApiClient } from '@signalvote/api-client';
import { ApiFailure, ErrorCode } from '@signalvote/shared';
import type { ImportBatchHistoryItem } from '@signalvote/shared';
import { skip } from '@signalvote/data';
import { defineQuery, defineWrite } from '@/lib/data';

// Import history, undo, and moving a bank-file import to another account. Undo and move both
// send the batch's token from its history row; a refusal for a stale token refreshes the history
// (it declares ImportBatch), so the next try carries the fresh one.

/** What a refused undo or move means for the treasurer. */
export type ImportBatchRefusal =
  | { kind: 'changedElsewhere' }
  | { kind: 'undoBlocked'; reasons: string[] }
  | { kind: 'periodLocked'; message: string; reportIds: string[] }
  | { kind: 'other'; message: string };

export function importBatchRefusal(error: unknown): ImportBatchRefusal {
  if (!(error instanceof ApiFailure)) return { kind: 'other', message: error instanceof Error ? error.message : String(error) };
  const { reason } = error;
  if (reason.kind === 'stale') return { kind: 'changedElsewhere' };
  if (reason.kind === 'refused') {
    if (reason.code === ErrorCode.UndoImportBlockedByDownstreamActions)
      return { kind: 'undoBlocked', reasons: stringList(error.body.blockingReasons) };
    if (reason.code === ErrorCode.ImportReattributeFiledReportPresent)
      return { kind: 'periodLocked', message: reason.message, reportIds: stringList(error.body.affectedReportIds) };
  }
  return { kind: 'other', message: error.message };
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

const importBatchHistoryRead = defineQuery({
  key: (limit: number) => ['finance', 'import-batches', 'history', limit],
  reads: 'GET /api/finance/import-batches/history',
  fetch: async (limit): Promise<ImportBatchHistoryItem[]> => (await getApiClient().importBatch.getHistory({ limit })).items,
});

/** The campaign's recent imports, newest first. */
export function useImportBatchHistory(limit = 50) {
  return importBatchHistoryRead(limit);
}

const undoPreviewRead = defineQuery({
  key: (batchId: string) => ['finance', 'import-batches', batchId, 'undo-preview'],
  reads: 'GET /api/finance/import-batches/{id}/undo-preview',
  fetch: (batchId) => getApiClient().importBatch.getUndoPreview(batchId),
});

/** What undoing the batch would delete; idle while no batch is chosen. */
export function useUndoPreview(batchId: string | null) {
  return undoPreviewRead(batchId ?? skip);
}

const useUndoImportWrite = defineWrite({
  row: 'ImportBatch',
  send: async (id, concurrencyVersion, body: { reason: string | null }): Promise<void> => {
    await getApiClient().importBatch.undo(id, { reason: body.reason, concurrencyVersion });
  },
});

export function useUndoImport() {
  const { mutateAsync, isPending } = useUndoImportWrite();
  return { undoImport: mutateAsync, isPending };
}

const useReattributeImportWrite = defineWrite({
  row: 'ImportBatch',
  send: async (id, concurrencyVersion, body: { newBankAccountId: string }): Promise<void> => {
    await getApiClient().importBatch.reattribute(id, {
      newBankAccountId: body.newBankAccountId,
      concurrencyVersion,
    });
  },
});

export function useReattributeImport() {
  const { mutateAsync, isPending } = useReattributeImportWrite();
  return { reattributeImport: mutateAsync, isPending };
}
